import React from 'react';
import noop from 'lodash/noop';
import { IconButton } from '@atlaskit/button/new';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import CrossCircleIcon from '@atlaskit/icon/utility/cross-circle';
import { Flex, Pressable, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { renderDateString } from '../../common/utils/date/date-picker';
import type { FuzzyDateProps } from '../../types';
import messages from './messages';

export const FuzzyDate = (props: FuzzyDateProps) => {
	const {
		onClick,
		onClearDate = noop,
		triggerProps = {},
		placeholder,
		value: date,
		withTimeScale,
	} = props;

	const { formatMessage } = useIntl();
	const locale = useLocale();

	const dateLabel = date
		? renderDateString(locale, date, placeholder, withTimeScale)
		: formatMessage(messages.noDateSet);

	return (
		<Flex gap="space.050" xcss={wrapperStyles}>
			<Pressable
				xcss={triggerStyles}
				{...triggerProps}
				onClick={onClick}
				testId="fuzzy-date-picker.ui.fuzzy-date.button"
			>
				{dateLabel}
			</Pressable>
			{date && withTimeScale ? (
				<IconButton
					appearance="subtle"
					spacing="compact"
					icon={CrossCircleIcon}
					onClick={onClearDate}
					label={formatMessage(messages.clearDate)}
				/>
			) : (
				<IconButton
					appearance="subtle"
					spacing="compact"
					icon={CalendarIcon}
					onClick={onClick}
					label={dateLabel}
				/>
			)}
		</Flex>
	);
};

const triggerStyles = xcss({
	width: '100%',
	height: '100%',
	textAlign: 'left',
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
	backgroundColor: 'color.background.neutral.subtle',
	':focus': {
		border: 'none',
		outline: 'none',
	},
});

const wrapperStyles = xcss({
	flex: 1,
	padding: 'space.050',
	alignItems: 'center',
	backgroundColor: 'color.background.input',
	border: `1px solid ${token('color.border.input')}`,
	borderRadius: token('border.radius', '3px'),
	transition: 'background-color 200ms ease-in-out, border-color 200ms ease-in-out',
	':hover': {
		cursor: 'pointer',
	},
	':focus-within': {
		borderColor: 'color.border.focused',
		backgroundColor: 'color.background.input.pressed',
		outlineColor: 'color.border.focused',
		outlineWidth: '1px',
		outlineStyle: 'solid',
	},
});
